import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import BottomModal from './BottomModel';

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reportData: ReportData) => void;
  title?: string;
}

interface ReportData {
  reason: string;
  description: string;
}

const ReportModal: React.FC<ReportModalProps> = ({
  open,
  onClose,
  onSubmit,
  title = "Report this listing"
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        reason: reason.trim(),
        description: reason.trim() // Use the same text for both fields
      });
      
      // Reset form
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <BottomModal
      open={open}
      onClose={handleClose}
      title={title}
      maxHeight="70vh"
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Help us understand what's wrong with this listing. Your report will be reviewed by our team.
        </Typography>

        <TextField
          fullWidth
          label="What's the issue?"
          placeholder="Please describe the problem with this listing"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 3 }}
          variant="outlined"
          size="small"
          multiline
          rows={4}
        />

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            fullWidth
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!reason.trim() || isSubmitting}
            fullWidth
            sx={{ 
              bgcolor: '#BA1A1A',
              '&:hover': {
                bgcolor: '#9A1515'
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Stack>
      </Box>
    </BottomModal>
  );
};

export default ReportModal;
